<template>
	<BModal id="booking-restart-modal"
		centered
		title="Restart Your Booking"
		size="lg">
		<template v-if="booking">
			<BForm>
				<template v-if="booking.service === 'Oneoff'">
					<BFormGroup label="What type of service are you looking for?" :state="getValidationState('oneoff_type')"
						:invalid-feedback="getValidationErrors('oneoff_type')">
						<BFormRadioGroup :model-value="oneoff_type" buttons
							button-variant="white" @update:model-value="oneoff_type = $event">
							<BFormRadio value="Bond">
								Bond Clean
							</BFormRadio>
							<BFormRadio value="Move In">
								Move In Clean
							</BFormRadio>
							<BFormRadio value="Spring">
								Spring Clean
							</BFormRadio>
						</BFormRadioGroup>
					</BFormGroup>
				</template>
				<template v-if="booking.service === 'Regular'">
					<BFormGroup label="Frequency" :state="getValidationState('frequency')">
						<div
							class="btn-group-toggle btn-group bv-no-focus-ring"
							role="radiogroup"
							aria-label="Booking frequency">
							<label
								class="btn btn-white mb-0 rounded-left-05"
								:class="{ active: frequency === 'Weekly' }"
								for="booking-restart-frequency-weekly">
								<input
									id="booking-restart-frequency-weekly"
									v-model="frequency"
									type="radio"
									name="booking-restart-frequency"
									value="Weekly"
									autocomplete="off">
								Weekly
							</label>
							<label
								class="btn btn-white mb-0 rounded-right-05"
								:class="{ active: frequency === 'Fortnightly' }"
								for="booking-restart-frequency-fortnightly">
								<input
									id="booking-restart-frequency-fortnightly"
									v-model="frequency"
									type="radio"
									name="booking-restart-frequency"
									value="Fortnightly"
									autocomplete="off">
								Fortnightly
							</label>
						</div>
						<BFormInvalidFeedback :state="getValidationState('frequency')">
							{{ getValidationErrors('frequency') }}
						</BFormInvalidFeedback>
					</BFormGroup>
				</template>
				<BFormRow>
					<BCol>
						<BFormGroup label="Days" :state="getValidationState('days')"
							:invalid-feedback="getValidationErrors('days')">
							<BFormInput :model-value="days" type="number"
								:min="0" :max="7"
								@update:model-value="days = $event" />
						</BFormGroup>
					</BCol>
					<BCol>
						<BFormGroup label="Hours" :state="getValidationState('hours')"
							:invalid-feedback="getValidationErrors('hours')">
							<BFormInput :model-value="hours" type="number"
								:min="0" :max="10"
								@update:model-value="hours = $event" />
						</BFormGroup>
					</BCol>
				</BFormRow>
				<BFormRow>
					<BCol>
						<BFormGroup label="Suburb" :state="getValidationState('suburb_id')"
							:invalid-feedback="getValidationErrors('suburb_id')">
							<multiselect
								ref="suburb_select" :model-value="suburb"
								class="booking-form-suburb-select"
								:options="suburbs"
								placeholder="Start typing to find your suburb"
								label="name"
								track-by="id"
								:internal-search="false"
								:clear-on-select="false"
								:preserve-search="true"
								@search-change="getSuburbs"
								@update:model-value="suburb = $event">
								<template #option="suburb">
									<span class="font-bold">{{ suburb.option.name }}</span> ({{ suburb.option.state }}
									{{ suburb.option.postcode }})
								</template>
							</multiselect>
						</BFormGroup>
					</BCol>
					<BCol>
						<BFormGroup label="Address" :state="getValidationState('address')"
							:invalid-feedback="getValidationErrors('address')">
							<BFormInput :model-value="address" @update:model-value="address = $event" />
						</BFormGroup>
					</BCol>
				</BFormRow>
				<BCollapse :visible="oldTotal !== newTotal">
					<BCard class="primary">
						<h3>New rates will apply</h3>
						<div class="my-2"></div>
						<BRow>
							<BCol>
								<h5 class="text-muted">
									Previous Rates
								</h5>
								<div class="text-muted">
									{{ formatCurrency(oldRate, { country: user.country }) }} per hour
								</div>
								<div class="text-muted">
									{{ formatCurrency(oldTotal, { country: user.country }) }} per clean
								</div>
							</BCol>
							<BCol cols="1">
								<BIcon icon="arrow-right" font-scale="2"
									class="text-primary" />
							</BCol>
							<BCol>
								<h5 class="text-primary">
									New Rates
								</h5>
								<div class="text-primary">
									{{ formatCurrency(newRate, { country: user.country }) }} per hour
								</div>
								<div class="text-primary">
									{{ formatCurrency(newTotal, { country: user.country }) }} per clean
								</div>
							</BCol>
						</BRow>
					</BCard>
				</BCollapse>
			</BForm>
		</template>
		<template #footer>
			<div class="d-flex justify-content-end gap-sm">
				<BButton variant="light" @click="$bvModal.hide('booking-restart-modal')">
					Close
				</BButton>
				<BButton variant="primary" :disabled="submitting"
					@click="handleSubmit">
					<BSpinner v-if="submitting" small
						class="mr-2"></BSpinner>
					Save Changes
				</BButton>
			</div>
		</template>
	</BModal>
</template>

<script>
	import Multiselect from 'vue-multiselect';
	import {formatCurrency, urlify} from '@js/utilities/index.js';
	import { useValidation } from '@js/composables/useValidation.js';

	export default {
		components: {
			Multiselect,
		},
		props: {
			booking: {
				type: Object,
				required: true,
			}
		},
		setup() {
			return {
				...useValidation(),
			};
		},
		data() {
			return {
				hours: this.booking.hours,
				days: this.booking.days,
				frequency: this.booking.frequency,
				oneoff_type: this.booking.oneoff_type,
				address: this.booking.address,
				suburb: this.booking.suburb,
				suburbs: [],
				submitting: false,
			}
		},
		computed: {
			oldRate() {
				const rate = this.getServiceRate(this.booking.suburb);

				if (!rate) {
					return null;
				}

				return ((rate.hourly_rate) + rate.agency_fee).toFixed(2)
			},
			oldTotal() {
				const rate = this.getServiceRate(this.booking.suburb);

				if (!rate) {
					return null;
				}

				return ((rate.hourly_rate * this.booking?.hours) + rate.agency_fee).toFixed(2)
			},
			newRate() {
				const rate = this.getServiceRate(this.suburb);

				if (!rate) {
					return null;
				}

				return ((rate.hourly_rate) + rate.agency_fee).toFixed(2)
			},
			newTotal() {
				const rate = this.getServiceRate(this.suburb);

				if (!rate) {
					return null;
				}

				return ((rate.hourly_rate * this.hours) + rate.agency_fee).toFixed(2)
			},
		},
		watch: {
			'booking.frequency': {
				immediate: true,
				handler(frequency) {
					this.frequency = frequency;
				},
			},
		},
		methods: {
			formatCurrency,
			getServiceRate(suburb) {
				const service = this.booking.service?.toLowerCase();
				const rates = suburb?.rates;

				if (!service || !rates) {
					return null;
				}

				return rates[service] || null;
			},
			async handleSubmit() {
				this.submitting = true;

				await this.$axios.post(`/api/client/bookings/${this.booking.unique_id}/restart`, {
					frequency: this.frequency,
					oneoff_type: this.oneoff_type,
					days: this.days,
					hours: this.hours,
					address: this.address,
					suburb_id: this.suburb.id,
				}).then((response) => {
					this.$bvToast.toast('Booking restarted', {
						title: 'Success',
						variant: 'success',
						solid: true,
					})

					this.clearErrors();

					this.$router.push({
						path: `/bookings/${response.data.data.unique_id}`,
					})
				}).catch(error => {
					this.handleError(error);
				})

				this.submitting = false;
			},
			async getSuburbs(query) {
				if (!query) return []
				if (this.cancelToken) this.cancelToken.cancel()

				const CancelToken = this.$axios.CancelToken
				const queryString = urlify(query)

				this.cancelToken = CancelToken.source()

				return this.$axios.get(`/api/suburbs/list/${queryString}`, {
					cancelToken: this.cancelToken.token,
					params: {
						country: this.country
					}
				}).then((response) => {
					this.suburbs = response.data
				}).catch((error) => {
					if (this.$axios.isCancel(error)) {
						return;
					}

					throw error;
				})
			}
		}
	}
</script>

<style lang="scss">
  @import '../../../../sass/my/multiselect.scss';
</style>
